import React, { useState, useEffect, useMemo } from 'react';
import { getIhsBasisComparison } from '../api/client';

const exportToCsv = (headers, rows, filename) => {
    const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(val => {
            const cleanVal = String(val ?? '').replace(/—/g, '-');
            return `"${cleanVal.replace(/"/g, '""')}"`;
        }).join(","))
    ].join("\n");
    // Prepend a UTF-8 BOM — without it, Excel/Numbers on Mac guesses MacRoman
    // for a plain CSV and mangles non-ASCII characters.
    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export default function IhsThresholdModal({ onClose, arps = [], obstacles = [] }) {
    const [selectedIcao, setSelectedIcao] = useState(arps[0]?.icao || '');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!selectedIcao) return;
        setLoading(true);
        setError(null);
        getIhsBasisComparison(selectedIcao)
            .then(res => setData(res.data))
            .catch(err => {
                console.error('Failed to load IHS basis comparison:', err);
                setError(err.response?.data?.detail || 'Failed to load IHS/threshold data.');
                setData(null);
            })
            .finally(() => setLoading(false));
    }, [selectedIcao]);

    const obsMargins = useMemo(() => {
        if (!data || !obstacles?.length) return [];
        return obstacles.map(o => {
            const elev = Number(o.elevation_m ?? o.elevation ?? 0);
            return {
                id: o.id,
                name: o.name || `Obstacle ${o.id}`,
                elev,
                margins: data.bases.map(b => +(b.ihs_elev_m - elev).toFixed(2)),
            };
        });
    }, [data, obstacles]);

    const handleExportCsv = () => {
        if (!data) return;
        const headers = ['Basis', 'Reference elevation used (m AMSL)', 'IHS elevation (m AMSL)'];
        const rows = data.bases.map(b => [b.label, b.elev_m, b.ihs_elev_m]);
        exportToCsv(headers, rows, `${data.icao}_IHS_basis_comparison.csv`);

        if (obsMargins.length) {
            const obsHeaders = ['Obstacle', 'Top Elev (m AMSL)', ...data.bases.map(b => `Margin vs ${b.label} (m)`)];
            const obsRows = obsMargins.map(o => [o.name, o.elev, ...o.margins]);
            exportToCsv(obsHeaders, obsRows, `${data.icao}_IHS_obstacle_margins.csv`);
        }
    };

    return (
        <div className="dg-modal-overlay">
            <div className="dg-modal" style={{ padding: 32, width: 720, maxHeight: '85vh', overflowY: 'auto' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>
                    IHS Elevation — ARP vs Threshold Basis
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 20 }}>
                    The Inner Horizontal Surface is always 45&nbsp;m above the reference elevation. The tool's default
                    reference is the aerodrome's ARP elevation. ICAO Annex&nbsp;14 defines "aerodrome elevation" as the
                    highest point of the landing area, which is not always the same figure as a database's ARP-point
                    record — this compares both.
                </div>

                {arps.length > 1 && (
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
                            Aerodrome
                        </div>
                        <select className="dg-input" value={selectedIcao} onChange={e => setSelectedIcao(e.target.value)}>
                            {arps.map(a => (
                                <option key={a.icao} value={a.icao}>{a.icao} — {a.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                {loading && (
                    <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading…</div>
                )}
                {error && (
                    <div style={{ padding: 12, color: '#ef4444', fontSize: 12 }}>{error}</div>
                )}

                {data && !loading && (
                    <>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 20 }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                                    {['Basis', 'Elevation used (m AMSL)', 'IHS elevation (m AMSL)'].map(h => (
                                        <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-tertiary)', fontWeight: 600 }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.bases.map(b => (
                                    <tr key={b.basis} style={{
                                        borderBottom: '1px solid var(--glass-border)',
                                        background: b.basis === 'arp' ? 'var(--blue-glass-fill)' : 'transparent',
                                    }}>
                                        <td style={{ padding: '6px 8px', color: 'var(--text-primary)' }}>
                                            {b.label}{b.basis === 'arp' ? ' (current default)' : ''}
                                        </td>
                                        <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{b.elev_m}</td>
                                        <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{b.ihs_elev_m}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {obsMargins.length > 0 && (
                            <>
                                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>
                                    Obstacle clearance margin against IHS, by basis
                                </div>
                                <div style={{ overflowX: 'auto', marginBottom: 8 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                        <thead>
                                            <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                                                <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-tertiary)' }}>Obstacle</th>
                                                <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-tertiary)' }}>Top elev</th>
                                                {data.bases.map(b => (
                                                    <th key={b.basis} style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-tertiary)' }}>{b.label}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {obsMargins.map(o => (
                                                <tr key={o.id} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                                                    <td style={{ padding: '6px 8px', color: 'var(--text-primary)' }}>{o.name}</td>
                                                    <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{o.elev}</td>
                                                    {o.margins.map((m, i) => (
                                                        <td key={i} style={{ padding: '6px 8px', color: m < 0 ? '#ef4444' : 'var(--text-secondary)', fontWeight: m < 0 ? 700 : 400 }}>
                                                            {m}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
                                    Margin = IHS elevation − obstacle top elevation under that basis. Assumes the obstacle
                                    falls within the IHS footprint — cross-check against the main OLS assessment for
                                    authoritative in/out status.
                                </div>
                            </>
                        )}
                    </>
                )}

                <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
                    <button className="dg-btn dg-btn-ghost" onClick={onClose}>Close</button>
                    <button
                        className="dg-btn dg-btn-primary"
                        onClick={handleExportCsv}
                        disabled={!data}
                        style={{ opacity: !data ? 0.5 : 1 }}
                    >
                        Export CSV
                    </button>
                </div>
            </div>
        </div>
    );
}
